/**
 * GridCrash — Store Catalog
 *
 * Client-side source of truth for everything purchasable. The server's
 * store_items table mirrors this list (see the content_expansion
 * migration); when it's reachable its prices win, but the shop renders
 * and sells from this catalog even fully offline.
 *
 * Also home to the "effective" economy helpers that combine the local
 * wallet with the synced profile — importing both is safe here because
 * nothing below imports catalog.ts back.
 */

import { getCachedProfile } from "./profile.js";
import { getWallet } from "./wallet.js";

export type CatalogKind = "theme" | "animation" | "mascot_pack" | "crate";

export interface CatalogItem {
  id: string;
  kind: CatalogKind;
  label: string;
  price: number;
  /** Emoji shown on the store card. */
  preview: string;
  description?: string;
}

/** Free defaults — always owned, never in store_items. */
export const DEFAULT_ASSETS = ["default", "fireworks"] as const;

export const CATALOG: CatalogItem[] = [
  // ── Board themes ──
  { id: "us_holiday", kind: "theme", label: "US Holiday", price: 50, preview: "🇺🇸" },
  { id: "winter",     kind: "theme", label: "Winter",     price: 50, preview: "🎄" },
  { id: "halloween",  kind: "theme", label: "Halloween",  price: 60, preview: "🎃" },
  { id: "ocean",      kind: "theme", label: "Ocean",      price: 60, preview: "🐚" },
  { id: "space",      kind: "theme", label: "Space",      price: 60, preview: "🛸" },
  { id: "retro",      kind: "theme", label: "Retro",      price: 60, preview: "🕹️" },
  { id: "neon",       kind: "theme", label: "Neon",       price: 60, preview: "⚡" },
  { id: "jungle",     kind: "theme", label: "Jungle",     price: 60, preview: "🌺" },

  // ── Win animations ──
  { id: "rockets",   kind: "animation", label: "Rockets",   price: 75, preview: "🚀" },
  { id: "rainbows",  kind: "animation", label: "Rainbows",  price: 75, preview: "🌈" },
  { id: "confetti",  kind: "animation", label: "Confetti",  price: 85, preview: "🎊" },
  { id: "coin_rain", kind: "animation", label: "Coin Rain", price: 85, preview: "🪙" },
  { id: "aurora",    kind: "animation", label: "Aurora",    price: 85, preview: "🌌" },
  { id: "supernova", kind: "animation", label: "Supernova", price: 85, preview: "💥" },

  // ── Mascot packs (extra header emojis for the picker) ──
  {
    id: "mascot_pack_animals", kind: "mascot_pack", label: "Animal Pack", price: 100, preview: "🐼",
    description: "10 animal mascots for your header",
  },
  {
    id: "mascot_pack_food", kind: "mascot_pack", label: "Snack Pack", price: 100, preview: "🍕",
    description: "10 food mascots for your header",
  },
];

/** Emojis granted by each mascot pack. */
export const MASCOT_PACKS: Record<string, string[]> = {
  mascot_pack_animals: ["🐼", "🐸", "🦖", "🐙", "🦉", "🐯", "🦈", "🐢", "🦜", "🐝"],
  mascot_pack_food:    ["🍕", "🌮", "🍩", "🍉", "🍦", "🥑", "🍔", "🍜", "🧁", "🍿"],
};

export function getCatalogItem(id: string): CatalogItem | undefined {
  return CATALOG.find((i) => i.id === id);
}

// ─── Effective economy (wallet ∪ profile) ─────────────────────────────────────

/** Spendable balance: synced server coins plus not-yet-migrated local coins. */
export function getEffectiveCoins(): number {
  const profileCoins = getCachedProfile()?.clash_coins ?? 0;
  return profileCoins + getWallet().coins;
}

/** Everything the player owns: defaults ∪ local purchases ∪ server purchases. */
export function getEffectiveOwned(): Set<string> {
  return new Set([
    ...DEFAULT_ASSETS,
    ...getWallet().unlocked,
    ...(getCachedProfile()?.unlocked_assets ?? []),
  ]);
}

/** Emojis from every owned mascot pack, in pack order. */
export function getOwnedPackEmojis(): string[] {
  const owned = getEffectiveOwned();
  const emojis: string[] = [];
  for (const [packId, packEmojis] of Object.entries(MASCOT_PACKS)) {
    if (owned.has(packId)) emojis.push(...packEmojis);
  }
  return emojis;
}
